import {
    type Department,
    GetTimeReportsParams,
    TimeEntryType,
    type TimeReport,
    formatNumber as fmtNum,
    getAllDepartmentPositions,
    getEmployeeName,
    getRange,
    monthNames,
    parseDateString,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import type { ChartDefinition } from "@pentacode/ui/charts";
import { barChart, chartSeriesColors, formatHours } from "@pentacode/ui/charts";
import "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { popover } from "../../directives/popover";
import { alert } from "../../elements/alert-dialog";
import "../../elements/scroller";
import { app } from "../../init";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import type { EmployeeShortInfo } from "./types";

const formatNumber = (val: number) => fmtNum(val, 0);

type SelectedEmployee = EmployeeShortInfo & { retired?: boolean };

@customElement("ptc-reports-time")
export class ReportsTime extends Routing(StateMixin(LitElement)) {
    routePattern = /^reports\/time/;

    get routeTitle() {
        return "Arbeitszeit-Bericht";
    }

    @state()
    private _loading = false;

    @state()
    private _reports: TimeReport[] = [];

    @state()
    private _selectedEmployee: SelectedEmployee | null = null;

    @state()
    private _selectedDepartment: Department | null = null;

    @state()
    private _filterString = "";

    @query("#filterInput")
    private _filterInput: HTMLInputElement;

    @state()
    private _chartMonthly?: ChartDefinition;

    @query(".venue-selector")
    private _venueSelector: HTMLSelectElement;

    private _cachedValues = new Map<string, number>();

    private readonly _plannedColor = "blue";
    private readonly _actualColor = "orange";

    private _updateFilter() {
        this._filterString = this._filterInput.value;
    }

    private _clearFilter() {
        this._filterString = this._filterInput.value = "";
    }

    handleRoute() {
        if (!this.venue || !app.hasAccess({ venue: this.venue })) {
            if (app.accessibleVenues.length) {
                this.go(null, { ...this.router.params, venue: app.accessibleVenues[0].id.toString() }, true);
            }
            return;
        }

        //Invalid date
        if (!this.date || !parseDateString(this.date)) {
            this.go(null, { ...this.router.params, date: toDateString(new Date()) }, true);
            return;
        }

        void this.load();
    }

    willUpdate(changes: Map<string, unknown>) {
        if (changes.has("_reports") || changes.has("_selectedEmployee") || changes.has("_selectedDepartment")) {
            this._chartMonthly = this._getChartDefinitionMonthly();
        }
    }

    async load() {
        if (!this.year || !this.venue) {
            return;
        }

        const range = getRange(`${this.year}-01-01` as DateString, "year");

        const currMonth = getRange(toDateString(new Date()), "month");

        if (range.to > currMonth.to) {
            range.to = currMonth.to;
        }

        this._loading = true;

        try {
            this._reports = await app.api.getTimeReports(
                new GetTimeReportsParams({
                    from: range.from,
                    to: range.to,
                    filters: [{ type: "venue", value: this.venueId }],
                    types: [TimeEntryType.Work],
                    resolution: "month",
                })
            );
            this._cachedValues.clear();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _selectVenue() {
        this.go(null, { venue: this._venueSelector.value, date: this.date });
    }

    private _getHours({
        departmentId,
        month,
        employeeId,
        mode,
    }: {
        departmentId?: number;
        month?: number;
        employeeId?: number;
        mode: "planned" | "final";
    }) {
        const key = `${departmentId}_${employeeId}_${month}_${mode}`;

        const cached = this._cachedValues.get(key);

        if (cached) {
            return cached;
        }

        let reports = employeeId ? this._reports.filter((r) => r.employeeId === employeeId) : this._reports;

        if (typeof month === "number") {
            reports = reports.filter(({ from }) => parseDateString(from)?.getMonth() === month);
        }

        let items = reports.flatMap((e) => e.items);

        if (typeof departmentId === "number") {
            const department = app.getDepartment(departmentId).department!;
            const allPositions = getAllDepartmentPositions(department);
            items = items.filter((item) => item.positionId && allPositions.some((p) => p.id === item.positionId));
        }

        let hours = 0;

        for (const item of items) {
            hours += mode === "planned" ? item.hoursPlanned : item.hours;
        }

        this._cachedValues.set(key, hours);

        return hours;
    }

    private _getChartDefinitionMonthly() {
        const employeeId = this._selectedEmployee?.id;
        const departmentId = this._selectedDepartment?.id;
        const series = [
            { name: "Geplant", mode: "planned" as const },
            { name: "Erfasst", mode: "final" as const },
        ];
        const rows = series.flatMap(({ name, mode }) =>
            monthNames.map((month, i) => ({
                x: month.slice(0, 3),
                series: name,
                value: this._getHours({ month: i, employeeId, departmentId, mode }),
            }))
        );

        return barChart(rows, {
            yAxis: "right",
            legend: false,
            format: formatHours,
            colors: { Geplant: chartSeriesColors[0], Erfasst: chartSeriesColors[2] },
        });
    }

    private _yearSelected(e: Event) {
        const date = parseDateString(this.date) || new Date();
        date.setFullYear(parseInt((e.target as HTMLSelectElement).value));
        this.go(null, { date: toDateString(date), venue: this._venueSelector.value });
    }

    private _selectEmployee(employee: SelectedEmployee | null) {
        this._selectedEmployee = employee;
        this._selectedDepartment = null;
        this.renderRoot.querySelector(".charts")!.scrollIntoView();
    }

    static styles = [
        shared,
        css`
            :host {
                display: grid;
                grid-template-columns: 200px 1fr;
            }

            .header {
                border-bottom: solid 1px var(--shade-2);
            }

            .main {
                position: relative;
            }

            .scroller {
                overflow: auto;
                margin: 0 0.5em;
            }

            .year-selector,
            .venue-selector {
                font-size: var(--font-size-medium);
                padding-top: 0.3em;
                padding-bottom: 0.3em;
            }

            .department-row {
                display: grid;
                grid-template-columns: 1fr 4em 4em;
                grid-gap: 0.5em;
            }

            .department-row-value {
                text-align: right;
            }

            button:not([active]) .department-row-value {
                color: var(--color-highlight);
            }

            .department-row-header {
                font-size: var(--font-size-tiny);
                color: var(--color-highlight);
                font-weight: bold;
                text-align: center;
                padding: 0.5em;
            }

            .charts {
                position: sticky;
                left: 0;
                display: flex;
                margin-top: 0.5em;
            }

            .charts-total {
                width: 19em;
            }

            .charts-monthly {
                flex: 1;
            }

            .charts-title {
                font-size: var(--font-size-large);
                padding: 0.5em;
                font-weight: 600;
                text-align: center;
            }

            .row {
                display: grid;
                grid-template-columns: 12em repeat(13, 1fr);
                background: var(--color-bg);
                min-width: 120em;
            }

            .row:not(:last-child) {
                border-bottom: solid 1px var(--shade-1);
            }

            .column {
                display: flex;
                min-width: 0;
            }

            :not(.header-row) > .column > * {
                flex: 1;
                font-size: var(--font-size-small);
                padding: 0.5em;
                text-align: right;
                min-width: 0;
                color: var(--color-highlight);
            }

            .column > :not(:last-child) {
                border-right: dashed 1px var(--shade-1);
            }

            .column:not(:last-child) {
                border-right: solid 1px var(--shade-1);
            }

            .header-row {
                position: sticky;
                top: 0;
                z-index: 1;
            }

            .header-column {
                text-align: left;
                position: sticky;
                left: 0;
                background: inherit;
                border-radius: inherit;
            }

            .header-row > .column {
                padding-top: 0.5em;
                font-weight: 600;
                border: none;
                text-align: center;
            }

            .header-row > .header-column {
                padding-left: 0;
                padding-bottom: 0.3em;
            }

            .filter-input input {
                font-weight: normal;
                padding-top: 0.4em;
                padding-bottom: 0.4em;
            }

            .row.total-row > .column {
                font-weight: bold;
            }

            .total-column {
                font-weight: 600;
                position: sticky;
                right: 0;
                background: inherit;
                border-left: solid 1px var(--shade-1) !important;
            }

            .row:not(.header-row):hover:not([active]) {
                background: #f1f1f1;
                cursor: pointer;
            }

            .row[active] {
                border-radius: var(--border-radius);
                border-color: transparent;
                background: var(--color-primary);
                color: var(--color-bg);
                position: sticky;
                top: 3.5em;
                bottom: 0.5em;
                z-index: 2;
                font-weight: bold;
            }

            .row[active] * {
                color: var(--color-bg) !important;
            }
        `,
    ];

    render() {
        if (!app.company || !this.venue) {
            return html``;
        }

        const years: number[] = [];
        for (let year = new Date().getFullYear(); year > 2010; year--) {
            years.push(year);
        }

        const displayedEmployees = app.accessibleEmployees.filter((e) =>
            getEmployeeName(e, "lastNameFirst").toLowerCase().includes(this._filterString.toLowerCase())
        );

        const employee = (this._selectedEmployee && this._selectedEmployee.id) || undefined;

        return html`
            <div class="fullbleed vertical layout">
                <div class="header horizontal center-aligning layout padded-medium noprint">
                    <div class="stretch"></div>

                    <select
                        class="venue-selector"
                        @change=${this._selectVenue}
                        ?hidden=${app.accessibleVenues.length < 2}
                    >
                        ${app.accessibleVenues.map(
                            (venue) => html`
                                <option .value=${venue.id.toString()} ?selected=${venue.id === this.venueId}>
                                    ${venue.name}
                                </option>
                            `
                        )}
                    </select>

                    <select class="year-selector" .value=${this.year.toString()} @change=${this._yearSelected}>
                        ${years.map((year) => html` <option .value=${year.toString()}>${year}</option> `)}
                    </select>
                </div>

                <div class="scroller stretch">
                    <div class="charts">
                        <div class="charts-total vertical layout">
                            <div class="charts-title">
                                ${(this._selectedEmployee &&
                                    getEmployeeName(this._selectedEmployee, "lastNameFirst")) ||
                                "Alle Mitarbeiter"}
                            </div>

                            <div class="department-row">
                                <div></div>
                                <div class="department-row-header ${this._plannedColor}">Geplant</div>
                                <div class="department-row-header ${this._actualColor}">Erfasst</div>
                            </div>

                            <ptc-scroller class="stretch collapse">
                                <div class="departments vertical tabs">
                                    <button
                                        class="department-row"
                                        ?active=${!this._selectedDepartment}
                                        @click=${() => (this._selectedDepartment = null)}
                                    >
                                        <div class="ellipsis">Alle Abteilungen</div>
                                        <div class="department-row-value ${this._plannedColor}">
                                            ${formatNumber(this._getHours({ mode: "planned", employeeId: employee }))}
                                        </div>
                                        <div class="department-row-value ${this._actualColor}">
                                            ${formatNumber(this._getHours({ mode: "final", employeeId: employee }))}
                                        </div>
                                    </button>

                                    ${[...this.venue.departments, ...(this.venue.archivedDepartments || [])].map(
                                        (dep) => {
                                            const planned = this._getHours({
                                                departmentId: dep.id,
                                                mode: "planned",
                                                employeeId: employee,
                                            });
                                            const actual = this._getHours({
                                                departmentId: dep.id,
                                                mode: "final",
                                                employeeId: employee,
                                            });

                                            if (!planned && !actual) {
                                                return;
                                            }

                                            return html`
                                                <button
                                                    class="department-row"
                                                    ?active=${this._selectedDepartment &&
                                                    this._selectedDepartment.id === dep.id}
                                                    @click=${() => (this._selectedDepartment = dep)}
                                                >
                                                    <div class="ellipsis">${dep.name}</div>
                                                    <div class="department-row-value ${this._plannedColor}">
                                                        ${formatNumber(planned)}
                                                    </div>
                                                    <div class="department-row-value ${this._actualColor}">
                                                        ${formatNumber(actual)}
                                                    </div>
                                                </button>
                                            `;
                                        }
                                    )}
                                </div>
                            </ptc-scroller>
                        </div>
                        <div class="charts-monthly">
                            <ptc-ui-chart
                                label="Geplante und erfasste Arbeitszeit pro Monat"
                                .height=${350}
                                .definition=${this._chartMonthly}
                            ></ptc-ui-chart>
                        </div>
                    </div>

                    <div class="table">
                        <div class="row header-row">
                            <div class="column header-column">
                                <div class="filter-input right icon input">
                                    <input
                                        id="filterInput"
                                        type="text"
                                        placeholder="Suchen..."
                                        @input=${this._updateFilter}
                                    />
                                    ${this._filterString
                                        ? html`<ptc-ui-icon
                                              name="icon.action.clear"
                                              class="click icon"
                                              @click=${this._clearFilter}
                                          ></ptc-ui-icon>`
                                        : html`<ptc-ui-icon
                                              name="icon.action.search"
                                              class="icon"
                                              @click=${this._clearFilter}
                                          ></ptc-ui-icon>`}
                                </div>
                            </div>

                            ${monthNames.map(
                                (n) => html`
                                    <div class="column vertical end-justifying layout">
                                        <div>${n.slice(0, 3)}</div>
                                        <div class="horizontal layout">
                                            <div class="stretch tiny highlight-fg ${this._plannedColor} padded-light">
                                                Geplant
                                            </div>
                                            <div class="stretch tiny highlight-fg ${this._actualColor} padded-light">
                                                Erfasst
                                            </div>
                                        </div>
                                    </div>
                                `
                            )}

                            <div class="column vertical end-justifying layout total-column">
                                <div>Gesamt</div>
                                <div class="horizontal layout">
                                    <div class="stretch tiny highlight-fg ${this._plannedColor} padded-light">
                                        Geplant
                                    </div>
                                    <div class="stretch tiny highlight-fg ${this._actualColor} padded-light">
                                        Erfasst
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            class="row total-row"
                            ?active=${!this._selectedEmployee}
                            @click=${() => this._selectEmployee(null)}
                        >
                            <div class="column header-column">
                                <div class="ellipsis">Alle Mitarbeiter</div>
                            </div>

                            ${monthNames.map(
                                (_name, month) => html`
                                    <div class="column">
                                        <div class="${this._plannedColor}">
                                            ${formatNumber(this._getHours({ month, mode: "planned" }))}
                                        </div>
                                        <div class="${this._actualColor}">
                                            ${formatNumber(this._getHours({ month, mode: "final" }))}
                                        </div>
                                    </div>
                                `
                            )}

                            <div class="column total-column">
                                <div class="${this._plannedColor}">
                                    ${formatNumber(this._getHours({ mode: "planned" }))}
                                </div>
                                <div class="${this._actualColor}">
                                    ${formatNumber(this._getHours({ mode: "final" }))}
                                </div>
                            </div>
                        </div>

                        ${displayedEmployees.map((employee) => {
                            const data = monthNames.map((_name, month) => ({
                                planned: this._getHours({ employeeId: employee.id, month, mode: "planned" }),
                                actual: this._getHours({ employeeId: employee.id, month, mode: "final" }),
                            }));

                            if (data.every((d) => !d.planned && !d.actual)) {
                                return null;
                            }

                            return html`
                                <div
                                    class="row"
                                    ?active=${this._selectedEmployee && this._selectedEmployee.id === employee.id}
                                    @click=${() => this._selectEmployee(employee)}
                                    style=${employee.active ? "" : "opacity: 0.75"}
                                    ${employee.active
                                        ? nothing
                                        : popover(html`Mitarbeiter ausgeschieden`, {
                                              class: "tooltip",
                                              trigger: "hover",
                                          })}
                                >
                                    <div class="column header-column">
                                        <div class="ellipsis">${getEmployeeName(employee, "lastNameFirst")}</div>
                                    </div>

                                    ${data.map(
                                        (val) => html`
                                            <div class="column">
                                                <div class="${this._plannedColor}">${formatNumber(val.planned)}</div>
                                                <div class="${this._actualColor}">${formatNumber(val.actual)}</div>
                                            </div>
                                        `
                                    )}

                                    <div class="column total-column">
                                        <div class="${this._plannedColor}">
                                            ${formatNumber(
                                                this._getHours({ employeeId: employee.id, mode: "planned" })
                                            )}
                                        </div>
                                        <div class="${this._actualColor}">
                                            ${formatNumber(this._getHours({ employeeId: employee.id, mode: "final" }))}
                                        </div>
                                    </div>
                                </div>
                            `;
                        })}
                    </div>
                </div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
